import {Button} from '@wandb/weave/components/Button';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import copyToClipboard from 'copy-to-clipboard';
import React, {FC, useContext} from 'react';

import {useIsInLoadedReportContext} from '../../pages/ReportPage/LoadedReportContext';
import {PanelBankUpdaterContext} from '../../state/panelbank/context';
import {RunQueryContext} from '../../state/runs/context';
import * as ViewHooks from '../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../state/views/panelBankSectionConfig/actions';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {nwToast} from '../../state/workspaces/namedWorkspaceToast';
import {isMobile} from '../../util/panelbankFlow';
import {ImageExportType} from '../../util/panelExport';
import {isExportable} from '../../util/panels';
import {getPanelSpec} from '../../util/panelTypes';
import {usePanelsSharedRefsContext} from '../Views/contexts/PanelsSharedRefsContext';
import {useDetectPanelVariant} from './hooks';
import {MovePanelSubmenu} from './MovePanelSubmenu';
import {usePanelContext} from './PanelContextProvider';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {PanelExportMenu} from './PanelExportMenu';
import {useSharePanelLink} from './StandalonePanel';
import {useSectionsForMovePanel} from './useSectionsForMovePanel';

export enum ExportType {
  API,
  CSV,
  Image,
}

enum Submenu {
  MOVE_PANEL,
  EXPORT_PANEL,
}

interface PanelActionsOverflowProps {
  defaultMoveToSectionRef?: PanelBankSectionConfigTypes.Ref;
  panelSpec: ReturnType<typeof getPanelSpec>;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  setExportType: (exportType: ExportType) => void;
  setImageExportType: (imageType: ImageExportType) => void;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isOpen: boolean;
  sharePanel: () => Promise<void>;
}

export const PanelActionsMenu: FC<PanelActionsOverflowProps> = ({
  defaultMoveToSectionRef,
  panelSpec,
  panelBankSectionConfigRef,
  setExportType,
  setImageExportType,
  setIsOpen,
  isOpen,
  sharePanel,
}) => {
  const runsContext = useContext(RunQueryContext);
  const {panelBankConfigRef, workspaceSettingsRef} =
    usePanelsSharedRefsContext();
  const {panelRef, trackPanelControls, isReadOnly, isProjectReadOnly} =
    usePanelContext();

  const sectionsForMovePanel = useSectionsForMovePanel();
  const isMovePanelAllowed =
    runsContext.report == null && sectionsForMovePanel.length > 0;

  const duplicatePanel = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.duplicatePanel
  );

  const deletePanel = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.deletePanel
  );
  const onRemovePanel = () => {
    deletePanel(panelRef, panelBankConfigRef, workspaceSettingsRef);
    trackPanelControls({action: 'Panel deleted'});
  };
  const {isFullScreenPanelPage} = useDetectPanelVariant();
  const sharePanelLink = useSharePanelLink();
  const {setExportingPanelRefID} = useContext(PanelBankUpdaterContext);
  const isReport = useIsInLoadedReportContext();

  const handleClose = () => {
    setIsOpen(false);
  };

  return !isMobile() ? (
    <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger>
        <Button
          active={isOpen}
          data-test="panel-action-icon-overflow"
          className="mr-8"
          icon="overflow-horizontal"
          variant="ghost"
          aria-label="More actions"
          size={isFullScreenPanelPage ? 'medium' : 'small'}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        {/* The zIndex is needed for reports, otherwise the menu is hidden behind the
        panel */}
        <DropdownMenu.Content style={{zIndex: 1}} align="start">
          {!isFullScreenPanelPage && !isProjectReadOnly && !isReport && (
            <DropdownMenu.Item
              data-test="add-to-report-button"
              onClick={() => {
                trackPanelControls({
                  action: 'Add to report clicked',
                });
                setExportingPanelRefID(panelRef.id);
              }}>
              <Icon name="add-to-report" /> Add panel to report
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item
            onClick={() => {
              const newUrl = sharePanelLink();
              copyToClipboard(`${window.location.origin}${newUrl}`);
              nwToast('Copied to clipboard');
              handleClose();
              trackPanelControls({action: 'Panel link copied'});
            }}>
            <Icon name="link-alt" />
            Copy panel URL
          </DropdownMenu.Item>
          {isExportable(panelSpec) && (
            <PanelExportMenu
              panelSpec={panelSpec}
              handleClose={handleClose}
              setExportType={setExportType}
              setImageExportType={setImageExportType}
            />
          )}
          {/* Share panel */}
          {!isReadOnly &&
            !isProjectReadOnly &&
            !isMobile() &&
            !isFullScreenPanelPage &&
            !isReport && (
              <DropdownMenu.Item
                data-test="share-panel-button"
                onClick={sharePanel}>
                <Icon name="users-team" /> Share panel in report...
              </DropdownMenu.Item>
            )}
          <DropdownMenu.Separator />
          {!isFullScreenPanelPage && (
            <DropdownMenu.Item
              data-test="panel-action-duplicate"
              onClick={() => {
                duplicatePanel(panelRef);
                handleClose();
                trackPanelControls({action: 'Panel duplicated'});
              }}>
              <Icon name="copy" />
              Duplicate panel
            </DropdownMenu.Item>
          )}
          {isMovePanelAllowed &&
            !isFullScreenPanelPage &&
            panelBankConfigRef &&
            panelBankSectionConfigRef && (
              <MovePanelSubmenu
                panelBankConfigRef={panelBankConfigRef}
                panelBankSectionConfigRef={panelBankSectionConfigRef}
                eligibleSections={sectionsForMovePanel}
                defaultMoveToSectionRef={defaultMoveToSectionRef}
              />
            )}
          {!isFullScreenPanelPage && <DropdownMenu.Separator />}
          <DropdownMenu.Item
            className="panel-action-delete"
            onClick={() => {
              onRemovePanel();
              handleClose();
            }}>
            <Icon name="delete" />
            Delete panel
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  ) : null;
};
